import React, {useEffect, useMemo, useRef} from 'react';
import {createPortal} from "react-dom";
import {useMutation} from "@apollo/client";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {Form, Formik, FormikHelpers, useFormikContext} from "formik";
import {Alert, Box, Button, Grid} from "@mui/material";
import {Typography} from "@mui/joy";
import {UpdateContaminationDeclaration} from "../../graphql/mutations/updateContaminationDeclaration";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import {
    CONTACT_INFORMATION_SECTION,
    CONTAMINATION_FIELDS,
    CONTAMINATION_SECTION,
    ContaminationDeclarationFormValues,
    DECLARATION_ACCEPTED_FIELD,
    DECONTAMINATED_FIELD,
    HARMFUL_SUBSTANCES_SECTION,
    isTextField,
    isYesNoField,
    MULTILINE_TEXT_FIELDS,
    SECTION_KEYS,
} from "../../utils/contaminationDeclaration/fields";
import {toFormValues} from "../../utils/contaminationDeclaration/formValues";
import {
    buildContaminationDeclarationUpdateInput,
    haveAnswersChanged,
} from "../../utils/contaminationDeclaration/buildUpdateInput";
import {
    getFormProgress,
    getPrintReadiness,
    groupAttributesBySection,
} from "../../utils/contaminationDeclaration/gating";
import {CatalogAttribute, getFallbackCatalog} from "../../utils/contaminationDeclaration/fallbackCatalog";
import {extractAnswerErrors} from "../../utils/contaminationDeclaration/serverErrors";
import {ContaminationDeclarationData} from "./types";
import SectionCard from "./SectionCard";
import SnapshotBlock from "./SnapshotBlock";
import FieldRenderer, {getFieldColumns} from "./FieldRenderer";
import StatusFooter from "./StatusFooter";
import FormStatusPanel from "./FormStatusPanel";

interface Props {
    declaration: ContaminationDeclarationData;
    canUpdate: boolean;
    /** Title / meta block rendered inside the centered form column. */
    header?: React.ReactNode;
    /**
     * DOM node of the screen's full-height sidebar. The progress panel is
     * portalled into it (keeping the Formik context) so the white column can
     * span from the app header down, beside the back button and the scroll
     * area. Without it the panel renders inline, sticky, right of the form.
     */
    sidebarContainer?: HTMLElement | null;
    onDirtyChange?: (dirty: boolean) => void;
}

const DECONTAMINATION_WARNING_CODE = 'DECONTAMINATION_NOT_CONFIRMED';

// Prototype proportions: a readable form column centered in the remaining
// space, the progress panel in a white sidebar at the right edge on wide
// screens (dropped below the `lg` breakpoint).
const FORM_MAX_WIDTH = 880;
const PANEL_WIDTH = 300;
const SECTION_GAP = '32px';

// Reports Formik's dirty state to the screen (for the back-button guard) and
// warns before the tab is closed with unsaved answers.
const DirtyTracker = ({onDirtyChange}: {onDirtyChange?: (dirty: boolean) => void}) => {
    const {dirty} = useFormikContext();
    useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
    useEffect(() => {
        if (!dirty) return;
        const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [dirty]);
    return null;
};

/**
 * Re-confirmation rule: once a saved, accepted declaration is edited, the
 * consent box is unticked so the editor confirms the changed content (and the
 * save carries `declarationAccepted: false` until they do). Fires once per
 * saved state — ticking the box again after the reset is respected.
 */
const ReconfirmOnChange = () => {
    const {values, initialValues, setFieldValue} = useFormikContext<ContaminationDeclarationFormValues>();
    const resetDoneRef = useRef(false);
    useEffect(() => { resetDoneRef.current = false; }, [initialValues]);
    useEffect(() => {
        if (resetDoneRef.current) return;
        if (!initialValues.declarationAccepted || !values.declarationAccepted) return;
        if (haveAnswersChanged(values, initialValues)) {
            resetDoneRef.current = true;
            setFieldValue(DECLARATION_ACCEPTED_FIELD, false);
        }
    }, [values, initialValues, setFieldValue]);
    return null;
};

const SetRemainingNoButton = ({disabled}: {disabled: boolean}) => {
    const {t} = useTranslation();
    const {values, setValues} = useFormikContext<ContaminationDeclarationFormValues>();
    const remaining = CONTAMINATION_FIELDS.filter((f) => values[f] === null || values[f] === undefined);
    if (remaining.length === 0) return null;
    return (
        <Button
            size="small"
            variant="outlined"
            disabled={disabled}
            onClick={() => setValues({...values, ...Object.fromEntries(remaining.map((f) => [f, false]))})}
            data-testid="doc-set-remaining-no"
        >
            {t('Set remaining to "No"')}
        </Button>
    );
};

const scrollToSection = (key: string) =>
    document.getElementById(`doc-section-${key}`)?.scrollIntoView({behavior: 'smooth', block: 'start'});

/**
 * The declaration editor (approach B of the prototype: one page, numbered
 * sections, status panel beside). One Formik context; the catalog (TDA list)
 * supplies section order, labels, required markers and quick picks, the typed
 * declaration fields supply the values. Saving sends only the answers that
 * changed.
 */
const ContaminationDeclarationForm = ({declaration, canUpdate, header, sidebarContainer, onDirtyChange}: Props) => {
    const {t} = useTranslation();
    const [updateDeclaration] = useMutation(UpdateContaminationDeclaration);

    const initialValues = useMemo(() => toFormValues(declaration), [declaration]);

    const attributes = useMemo<CatalogAttribute[]>(() => {
        const fromBackend = (declaration.typeDefinitionAttributeList?.typeDefinitionAttributes ?? [])
            .filter((a): a is CatalogAttribute => !!a?.index);
        return fromBackend.length > 0 ? fromBackend : getFallbackCatalog();
    }, [declaration.typeDefinitionAttributeList]);
    const sections = useMemo(() => groupAttributesBySection(attributes), [attributes]);
    const knownIndices = useMemo(() => attributes.map((a) => a.index), [attributes]);

    const serverWarnings = useMemo(
        () => (declaration.warningList ?? []).filter((w): w is NonNullable<typeof w> => !!w),
        [declaration.warningList],
    );

    const warningTextFor = (index: string, values: ContaminationDeclarationFormValues): string | null => {
        // Live mirror: the "not freed from danger" notice appears as soon as
        // "No" is chosen, not only after the save round trip.
        if (index === DECONTAMINATED_FIELD && values[DECONTAMINATED_FIELD] === false) {
            return t(DECONTAMINATION_WARNING_CODE);
        }
        const warning = serverWarnings.find((w) => w.attributeIndex === index);
        if (!warning) return null;
        const translated = t(warning.code);
        return translated !== warning.code ? translated : warning.message;
    };

    const handleSubmit = async (
        values: ContaminationDeclarationFormValues,
        helpers: FormikHelpers<ContaminationDeclarationFormValues>,
    ) => {
        const {input, errors, hasChanges} = buildContaminationDeclarationUpdateInput(values, initialValues);
        if (Object.keys(errors).length > 0) {
            helpers.setErrors(errors as Record<string, string>);
            helpers.setSubmitting(false);
            return;
        }
        if (!hasChanges) {
            helpers.setSubmitting(false);
            return;
        }
        try {
            const res = await updateDeclaration({variables: {id: declaration.id, input}});
            const updated = res.data?.updateContaminationDeclaration;
            toast.success(t('Declaration saved'));
            if (updated) helpers.resetForm({values: toFormValues(updated)});
        } catch (err) {
            toast.error(resolveErrorMessage(err, t));
            const fieldErrors = extractAnswerErrors((err as {message?: string})?.message, knownIndices);
            if (Object.keys(fieldErrors).length > 0) helpers.setErrors(fieldErrors);
        } finally {
            helpers.setSubmitting(false);
        }
    };

    const disabled = !canUpdate;

    /**
     * Lays a section's questions out in the grid. Consecutive question rows
     * share one full-width item so they read as a divided list (prototype
     * rows) instead of loose cards separated by grid gaps. In sections that
     * mix yes/no questions with short answers ("Oil drained" → "Oil type")
     * the short answers join the list as label-left / input-right rows.
     */
    const renderFields = (
        sectionAttributes: CatalogAttribute[],
        sectionTitle: string,
        openSet: Set<string>,
        values: ContaminationDeclarationFormValues,
    ): React.ReactNode[] => {
        const listMode = sectionAttributes.some((a) => isYesNoField(a.index));
        const isRow = (attribute: CatalogAttribute) =>
            isYesNoField(attribute.index)
            || (listMode && isTextField(attribute.index) && !MULTILINE_TEXT_FIELDS.includes(attribute.index));

        const field = (attribute: CatalogAttribute) => (
            <FieldRenderer
                key={attribute.index}
                attribute={attribute}
                missing={openSet.has(attribute.index)}
                disabled={disabled}
                warning={warningTextFor(attribute.index, values)}
                sectionTitle={sectionTitle}
                layout={isRow(attribute) ? 'row' : 'grid'}
            />
        );

        const items: React.ReactNode[] = [];
        let rowRun: CatalogAttribute[] = [];
        const flushRowRun = () => {
            if (rowRun.length === 0) return;
            items.push(
                <Grid item xs={12} key={`rows-${rowRun[0].index}`}>
                    <Box sx={{display: 'flex', flexDirection: 'column'}}>{rowRun.map(field)}</Box>
                </Grid>,
            );
            rowRun = [];
        };

        sectionAttributes.forEach((attribute) => {
            if (isRow(attribute)) {
                rowRun.push(attribute);
                return;
            }
            flushRowRun();
            items.push(
                <Grid item xs={12} md={getFieldColumns(attribute)} key={attribute.index}>
                    {field(attribute)}
                </Grid>,
            );
        });
        flushRowRun();
        return items;
    };

    return (
        <Formik<ContaminationDeclarationFormValues>
            initialValues={initialValues}
            enableReinitialize
            onSubmit={handleSubmit}
        >
            {({values, dirty, isSubmitting, submitCount}) => {
                // Every section is asked: the harmful-substances gate went with Rev. 2 of
                // the form, which requires the table "even if only air exposure". The
                // predicate stays a parameter because `getFormProgress` is the one place
                // that knows what "active" means for the progress bar.
                const progress = getFormProgress(sections, values, () => true);
                // Per-field "not answered" hints only after the user tried to save — a fresh
                // declaration has every required question open, flagging all of them is noise.
                const flagOpen = submitCount > 0 || !!declaration.updatedAt;
                const openSet = new Set(flagOpen ? progress.sections.flatMap((s) => s.openIndices) : []);

                // The PDF is rendered from the *saved* answers, so unsaved edits never
                // flip the form to "ready". The live open count is stricter than the
                // backend (substances are required once the section is active) and must
                // be zero too, so footer, sidebar and section chips never disagree.
                const printReady = !dirty
                    && getPrintReadiness(declaration.validity, values.declarationAccepted).ready
                    && progress.totalOpen === 0;
                const printBlockedReason = dirty
                    ? t('Save the declaration before printing.')
                    : t('Answer all questions and accept the declaration before printing.');

                const panel = (
                    <Box sx={{px: '24px', pt: '20px', pb: '24px'}}>
                        <FormStatusPanel
                            progress={progress}
                            onJump={scrollToSection}
                            declarationId={declaration.id}
                            ready={printReady}
                            printBlockedReason={printBlockedReason}
                        />
                    </Box>
                );

                // The form element is the flex row so the inline sidebar fallback
                // can stretch to the full scroll height (at least the viewport).
                return (
                    <Form noValidate data-testid="doc-form" style={{display: 'flex', alignItems: 'stretch', minHeight: '100%'}}>
                        <DirtyTracker onDirtyChange={onDirtyChange}/>
                        <ReconfirmOnChange/>

                        <Box sx={{flex: 1, minWidth: 0, px: {xs: '12px', md: '24px'}, pt: '4px', pb: '12px'}}>
                                <Box sx={{width: '100%', maxWidth: FORM_MAX_WIDTH, mx: 'auto', display: 'flex', flexDirection: 'column'}}>
                                    {header}

                                    {disabled && (
                                        <Alert severity="info" sx={{mb: '20px'}}>
                                            {t('You can view this declaration but not edit it.')}
                                        </Alert>
                                    )}

                                    <Box sx={{display: 'flex', flexDirection: 'column', gap: SECTION_GAP}}>
                                        {sections.map((section, i) => {
                                            const sectionProgress = progress.sections[i];
                                            const isKnownSection = (SECTION_KEYS as readonly string[]).includes(section.key);

                                            return (
                                                <SectionCard
                                                    key={section.key}
                                                    number={i + 1}
                                                    title={section.title}
                                                    status={sectionProgress.status}
                                                    openCount={sectionProgress.openIndices.length}
                                                    testId={`doc-section-${section.key}`}
                                                    hint={section.key === CONTACT_INFORMATION_SECTION
                                                        ? t('Pre-filled from the customer record – please verify.')
                                                        : undefined}
                                                    headerAction={section.key === CONTAMINATION_SECTION
                                                        ? <SetRemainingNoButton disabled={disabled}/>
                                                        : undefined}
                                                >
                                                    {section.key === SECTION_KEYS[0] && <SnapshotBlock declaration={declaration}/>}

                                                    {section.key === HARMFUL_SUBSTANCES_SECTION && (
                                                        <Typography level="body-sm" sx={{color: 'text.tertiary', mb: 2}}>
                                                            {t('List every substance, gas and by-product that came into contact with the asset. This has to be filled in even if the asset only saw air – one entry reading "Air" is the answer then.')}
                                                        </Typography>
                                                    )}

                                                    {/* Same vertical rhythm as the library's TDAForm (rowSpacing 3 = 24px),
                                                        which gives floating labels their breathing room above the field. */}
                                                    <Grid container rowSpacing={3} columnSpacing={2}>
                                                        {renderFields(section.attributes, section.title, openSet, values)}
                                                    </Grid>

                                                    {!isKnownSection && (
                                                        <Typography level="body-xs" sx={{color: 'text.tertiary', mt: 1}}>
                                                            {t('This section was added to the template after this app version was built and is shown read-only.')}
                                                        </Typography>
                                                    )}
                                                </SectionCard>
                                            );
                                        })}
                                    </Box>

                                    <StatusFooter
                                        declarationId={declaration.id}
                                        ready={printReady}
                                        printBlockedReason={printBlockedReason}
                                        declarationAccepted={values.declarationAccepted}
                                        openCount={progress.totalOpen}
                                        updatedAt={declaration.updatedAt}
                                        updatedByName={declaration.updatedBy?.name}
                                        canUpdate={canUpdate}
                                        dirty={dirty}
                                        submitting={isSubmitting}
                                    />
                                </Box>
                            </Box>

                        {sidebarContainer ? createPortal(panel, sidebarContainer) : (
                            // Inline fallback: white column whose content sticks to the top
                            // of the scroll container (the detail screen's overflow box).
                            <Box
                                sx={{
                                    width: PANEL_WIDTH, flexShrink: 0,
                                    display: {xs: 'none', lg: 'block'},
                                    backgroundColor: '#fff', borderLeft: '1px solid #e0e0e0',
                                }}
                            >
                                <Box sx={{position: 'sticky', top: 0}}>{panel}</Box>
                            </Box>
                        )}
                    </Form>
                );
            }}
        </Formik>
    );
};

export default ContaminationDeclarationForm;
